<template>
  <div v-if="show" class="root-layout" :class="rootLayoutClass" :style="customStyle">
    <AsideIconMenu v-if="showAsideMenu"></AsideIconMenu>
    <PersonalCommonHeader
      class="common-header"
      :companyId="companyId"
      :customPage="customPage"
      @changeLanguage="handleChangeLanguage"
    ></PersonalCommonHeader>
    <div class="main-content">
      <router-view></router-view>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import AsideIconMenu from './asideIconMenu.vue'
import consentRoute from '@/views/personalNew/home/consentRoute'
import personalHomeRoute from '@/views/personalNew/home/homeRoute'
import personalProfileRoute from '@/views/personalNew/home/profileRoute'
import personalReferralRoute from '@/views/personalNew/home/referralsRoute'
import PersonalCommonHeader from '@/components/layout/header/personalCommonHeader.vue'
import { mapActions, mapGetters } from 'vuex'
import mixin from './mixin'
import { addI18nProperty } from '@/i18n'

const baseStyle = `
* {
  -webkit-box-sizing: border-box;
          box-sizing: border-box;
}
::-webkit-scrollbar {
  width: 18px;
  height: 18px;
  border-radius: 10px;
  background: #fafafa;
}
::-webkit-scrollbar-thumb {
  border: 4px solid transparent;
  border-radius: 10px;
  background: #c3c5c9;
  background-clip: content-box;
}
`

const elStyle = `
.el-select-dropdown.el-popper {
  border-color: #dcdcdc;
  -webkit-box-shadow: -1px 3px 4px 0px rgba(234, 234, 234, 0.5);
          box-shadow: -1px 3px 4px 0px rgba(234, 234, 234, 0.5);
}
.el-select-dropdown.el-popper[x-placement^=bottom] {
  margin-top: -1px;
}
.el-select-dropdown.el-popper[x-placement^=top] {
  margin-bottom: -1px;
}
.el-select-dropdown.el-popper .popper__arrow {
    display: none;
}
.no-arrow-dropdown-menu.el-popper[x-placement^=bottom] {
  margin-top: 0;
}
.no-arrow-dropdown-menu.el-popper[x-placement^=top] {
  margin-bottom: 0;
}
.no-arrow-dropdown-menu.el-popper .popper__arrow {
  display: none;
}
.el-popover.light-popper {
  color: #606266;
  font-size: 14px;
  line-height: 22px;
}
.el-popover.light-popper,
.el-tooltip__popper.is-dark.light-tooltip-popper {
  border-color: #e6e6e6;
  -webkit-box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
          box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
}
.el-popover.light-popper[x-placement] .popper__arrow,
.el-tooltip__popper.is-dark.light-tooltip-popper[x-placement] .popper__arrow {
  border-bottom-color: #e6e6e6;
  border-top-color: #e6e6e6;
}
.el-popover.light-popper[x-placement] .popper__arrow::after,
.el-tooltip__popper.is-dark.light-tooltip-popper[x-placement] .popper__arrow::after  {
  border-bottom-color: #e6e6e6;
  border-top-color: #e6e6e6;
}
.el-select {
  width: 100%;
}
.el-select .el-input .el-input__icon {
  line-height: inherit;
}
.el-select .el-input.is-focus .el-input__inner {
  border-color: #dcdcdc;
}
.el-input .el-input__inner {
  height: 32px;
  line-height: 32px;
  color: #232323;
}
.el-input .el-input__inner:focus {
  border-color: #dcdcdc;
}
.el-input .el-input__inner:hover {
  border-color: #C0C4CC;
}
.el-input.el-input--prefix .el-input__prefix .el-input__icon {
  line-height: 34px;
}
.el-input.el-input--suffix .el-input__inner {
  padding-right: 15px;
}
.el-input.el-input--suffix:hover .el-input__inner {
  padding-right: 30px;
}
.el-input.el-input--suffix .el-input__suffix .el-input__icon {
  line-height: inherit;
}
.el-input.password-input .el-input__inner {
  padding-right: 50px;
}
.el-input.password-input .el-input__suffix {
  right: 20px;
}
.el-input.is-disabled .el-input__inner {
  background-color: #F5F7FA;
  border-color: #E4E7ED;
  color: #C0C4CC;
  cursor: not-allowed;
}
.el-autocomplete {
  width: 100%;
}
.el-textarea .el-textarea__inner {
  padding: 5px;
  border-radius: 0;
}
.el-date-editor {
  width: 100%;
}
`

export default {
  components: {
    AsideIconMenu,
    PersonalCommonHeader
  },
  metaInfo () {
    const style = [{
      cssText: baseStyle,
      callback: () => {
        this.show = true
      }
    }]
    if (this.hasElStyle) {
      style.push({
        cssText: elStyle,
        callback: () => {
          this.elStyleShow = true
        }
      })
    }
    return { style: style }
  },
  mixins: [mixin],
  data () {
    return {
      show: false,
      elStyleShow: false,
      hasElStyle: true,
      loadThemePromise: null,
      customPage: {}
    }
  },
  computed: {
    rootLayoutClass () {
      if (this.$route.meta.rootLayout) {
        const layoutSetting = this.$route.meta.rootLayout
        return [
          layoutSetting.hiddenBackgroundImage && 'hidden-background-image',
          layoutSetting.hiddenTabList && 'hidden-tab-list',
          layoutSetting.flexMode && 'flex-layout',
          layoutSetting.autosize && 'auto-size',
          layoutSetting.hiddenLeftPadding && 'hidden-left-padding'
        ]
      }
      return null
    },
    customStyle () {
      return {
        '--header-height': this.headerSetting.headerHeight ? `${this.headerSetting.headerHeight}px` : this.hasCustomTheme ? '124px' : null
      }
    },
    showAsideMenu () {
      if (this.$route.meta.hiddenAsideMenuLayout || this.$route.meta.hiddenAsideMenu) {
        return false
      }
      return true
    },
    menuList () {
      const menuMap = {
        applications: { route: personalHomeRoute, icon: 'iconfont icona-MyApplications' },
        profile: { route: personalProfileRoute, icon: 'iconfont icona-MyProfile' },
        referral: { route: personalReferralRoute, icon: 'iconfont icona-TrackReferralRewards' },
        policy: { route: consentRoute, icon: 'iconfont icona-PrivacyPolicy' }
        // aboutReferral: { path: '/referral/about-program/', icon: 'iconfont icona-AboutReferralProgram' },
        // changeEmail: { path: '/request-change-email/', icon: 'iconfont icona-ChangeEmail2' }
      }

      const menuList = {}
      const account = this.getCurrentAccount()
      const showReferralRewards = account.employee?.company.root_company.id !== 12600
      _.forEach(menuMap, (menu, key) => {
        if (!this.hasAnyPermission(menu.permission || menu.route?.meta?.permission)) {
          return
        }
        if (menu === menuMap.aboutReferral && !showReferralRewards) {
          return
        }
        menuList[key] = menu
      })
      return menuList
    },
    activeKey () {
      const metaInfoList = [this.$route.meta]
      let metaInfo = this.$route.meta
      while ((metaInfo = metaInfo._parent?.meta)) {
        metaInfoList.push(metaInfo)
      }
      return _.findKey(this.menuList, (item) => {
        if (item.route && item.route.meta) {
          return metaInfoList.includes(item.route.meta)
        }
        return false
      })
    },
    companyId () {
      return this.$route.query.company_id
    }
  },
  async created () {
    this.$eventBus.$on('hideElStyle', this.hideElStyle)
    this.loadCompanyPageInfo()
  },
  mounted () {
    this.$nextTick(() => {
      setTimeout(() => {
        this.show = true
      }, 500)
    })
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('company', ['getCompanyTheme', 'getCompanyBrandingPageInfo']),
    async loadCompanyPageInfo () {
      if (!this.companyId) {
        return
      }
      const resp = await this.getCompanyBrandingPageInfo({
        company_id: this.isMSDCompany ? this.MSDCompanyId : this.companyId,
        language_code: this.$i18n.locale === 'en' ? 'en' : 'zh-cn'
      })
      if (resp) {
        const brandingPage = resp.branding_page
        const landingPage = resp.landing_page
        if (brandingPage) {
          addI18nProperty(brandingPage, 'i18nName', 'menu_cn_name', 'menu_en_name')
        }
        if (landingPage) {
          addI18nProperty(landingPage, 'i18nName', 'menu_cn_name', 'menu_en_name')
        }
        this.customPage = resp
      }
    },
    async handleChangeLanguage () {
      this.$eventBus.$emit('showLoading', true)
      try {
        await this.loadCompanyPageInfo()
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    hideElStyle (isHide) {
      this.hasElStyle = !isHide
    }
  }
}
</script>

<style lang="scss" scoped>
.root-layout {
  display: flex;
  width: 100vw;
  height: 100vh;
  padding-left: 56px;
  background: #f8f8f8;
  line-height: normal;
  font-size: 14px;
  color: #333333;
  flex-direction: column;
  position: relative;

  .aside-icon-menu {
    z-index: 2;
  }

  .main-content {
    flex: 1;
    overflow: auto;
    padding: 20px;
  }

  .light-header {
    padding-left: 15px;
    height: var(--header-height, 56px);
    line-height: var(--header-height, 56px);
  }

  &.flex-layout {
    width: auto;
    height: auto;
    min-width: 100%;
    padding: 0;

    .main-content {
      overflow: initial;
    }
  }

  &.hidden-tab-list {
    .main-content {
      width: auto;
      margin: 0;
      padding: 40px 80px;
    }
  }

  &.hidden-background-image {
    background-color: #fdfdf6;
  }

  &.hidden-left-padding {
    .main-content {
      padding-left: 0;
    }
  }
}
</style>
